import React, {
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { FieldType } from 'utils/enum';
import { useParams } from 'react-router-dom';
import { useRLTemplateAndSupplierFieldConfig } from 'queries/estimates';
import { processFields, processInitialValues, processSchema } from 'utils/form';
import { Layout } from 'components/Form';
import {
  useBidTypeChoices,
  useRepChoices,
  useRLColorChoices,
} from 'queries/company';
import { useAllRLTemplateChoices, useJobSupplierChoices } from 'queries/job';
import useAuth from 'hooks/useAuth';
import { find } from 'lodash';
import { wastePctChoices } from 'pages/RoofTemplates/utils';

export interface IFormValues {
  rep: number;
  bid_type: string;
  supplier?: number;
  supplier_location?: number;
  srs_job_account?: number;
  template: number;
  color: string;
  waste_pct_choice?: string;
  custom_waste_pct?: number;
}

export interface IFormPayload extends Omit<IFormValues, 'srs_job_account'> {
  job: string;
  version?: string;
  srs_job_account?: {
    jobAccountNumber: string;
    jobAccountName: string;
  };
}

export type DrawingFormRef = {
  getFormData: () => Promise<IFormPayload | null>;
  validate: () => boolean;
  checkValidity: () => boolean;
  handleSubmit: (
    onSubmit: (data: IFormValues) => void
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
  reset: (values?: Partial<IFormValues>) => void;
  setValue: (name: keyof IFormValues, value: any) => void;
};

type FormProps = {
  jobId?: string;
  loading?: boolean;
  showRepField?: boolean;
  showBidTypeField?: boolean;
  onRepSelect?: (rep: number) => void;
  onBidTypeSelect?: (bidType: string) => void;
  onFormChange?: () => void;
  sharedRep?: number;
  sharedBidType?: string;
  initialRep?: number;
  initialBidType?: string;
};

const DrawingForm = forwardRef<DrawingFormRef, FormProps>(
  (
    {
      jobId: propJobId,
      loading = false,
      showRepField = true,
      showBidTypeField = true,
      onRepSelect,
      onBidTypeSelect,
      onFormChange,
      sharedRep,
      sharedBidType,
      initialRep,
      initialBidType,
    },
    ref
  ) => {
    const { jobId: paramJobId = '' } = useParams();
    const jobId = propJobId || paramJobId;
    const { data: config, isLoading } =
      useRLTemplateAndSupplierFieldConfig(jobId);
    const { data: supplierChoices } = useJobSupplierChoices(jobId);

    const { userInfo } = useAuth();
    const [supplier, setSupplier] = useState<any>(null);
    const [supplierLocation, setSupplierLocation] = useState<any>(null);
    const [srsJobAccount, setSrsJobAccount] = useState<any>(null);
    const [isSupplierFrozen, setIsSupplierFrozen] = useState(false);
    const [templateVersion, setTemplateVersion] = useState<string | undefined>(
      undefined
    );

    const isUpdatingRepFromSharedRef = useRef(false);
    const isUpdatingBidTypeFromSharedRef = useRef(false);

    const schema = yup.object(
      processSchema(
        {
          template: yup.number(),
          rep: yup.number(),
          supplier: yup.number().nullable().optional(),
          color: yup.string(),
          bid_type: yup.string(),
          supplier_location: yup.number().nullable().optional(),
          srs_job_account: yup.number().nullable().optional(),
          waste_pct_choice: yup.string().optional(),
          custom_waste_pct: yup.number().optional(),
        },
        config,
        [
          'supplier_location',
          'srs_job_account',
          'waste_pct_choice',
          'custom_waste_pct',
        ]
      )
    );

    const {
      control,
      handleSubmit,
      reset,
      formState: { errors },
      setError,
      setValue,
      getValues,
      watch,
    } = useForm<IFormValues>({
      resolver: yupResolver(schema),
      defaultValues: {
        rep: initialRep || sharedRep,
        bid_type: initialBidType || sharedBidType,
      },
    });

    // Watch for rep, bid_type, template and waste_pct_choice changes
    const repValue = watch('rep');
    const bidTypeValue = watch('bid_type');
    const templateValue = watch('template');
    const wastePctChoice = watch('waste_pct_choice');

    // Notify parent when form values change
    useEffect(() => {
      onFormChange?.();
    }, [templateValue, repValue, bidTypeValue, onFormChange]);

    useEffect(() => {
      if (config) {
        const defaultValues = processInitialValues(config);
        reset({
          ...defaultValues,
          rep: initialRep || sharedRep || defaultValues.rep,
          bid_type: initialBidType || sharedBidType || defaultValues.bid_type,
        });
      }
    }, [config]);

    // Sync from sharedRep to form (for auto mode)
    useEffect(() => {
      if (isUpdatingRepFromSharedRef.current) {
        isUpdatingRepFromSharedRef.current = false;
        return;
      }

      if (sharedRep && (!repValue || repValue !== sharedRep)) {
        isUpdatingRepFromSharedRef.current = true;
        setValue('rep', sharedRep, { shouldDirty: false });
      }
    }, [sharedRep, repValue, setValue]);

    // Sync from sharedBidType to form (for auto mode)
    useEffect(() => {
      if (isUpdatingBidTypeFromSharedRef.current) {
        isUpdatingBidTypeFromSharedRef.current = false;
        return;
      }

      if (sharedBidType && (!bidTypeValue || bidTypeValue !== sharedBidType)) {
        isUpdatingBidTypeFromSharedRef.current = true;
        setValue('bid_type', sharedBidType, { shouldDirty: false });
      }
    }, [sharedBidType, bidTypeValue, setValue]);

    // Initialize form with initial values (for auto mode)
    useEffect(() => {
      if (initialRep && !repValue) {
        setValue('rep', initialRep);
      }
      if (initialBidType && !bidTypeValue) {
        setValue('bid_type', initialBidType);
      }
    }, [initialRep, initialBidType, setValue, repValue, bidTypeValue]);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      getFormData: async () => {
        const values = getValues();
        // Use shared values if form doesn't have them (auto mode)
        const finalRep = values.rep || sharedRep || initialRep;
        const finalBidType = values.bid_type || sharedBidType || initialBidType;

        if (!values.template) {
          setError('template', {
            message: 'Please select a template',
          });
          return null;
        }

        if (!finalRep || !finalBidType) {
          if (!finalRep && showRepField) {
            setError('rep', { message: 'Please select a rep' });
          }
          if (!finalBidType && showBidTypeField) {
            setError('bid_type', { message: 'Please select a bid type' });
          }
          return null;
        }

        // Validate supplier location and srs job account
        if (supplier?.integrated) {
          if (!supplierLocation) {
            setError('supplier_location', {
              message: 'Please select a supplier location',
            });
            return null;
          }

          if (supplier.supplier_code === 'SRS_SUPPLY' && !srsJobAccount) {
            setError('srs_job_account', {
              message: 'Please select a SRS job account',
            });
            return null;
          }
        }

        // Extract numeric ID from template value (e.g., "v2_123" -> 123)
        const templateId = values.template
          ? Number(String(values.template).split('_')[1])
          : values.template;

        return {
          ...values,
          template: templateId,
          rep: finalRep,
          bid_type: finalBidType,
          version: templateVersion,
          srs_job_account: srsJobAccount
            ? {
                jobAccountNumber: srsJobAccount['value'],
                jobAccountName: srsJobAccount['name'],
              }
            : undefined,
          job: jobId,
        };
      },
      validate: () => {
        const values = getValues();
        const finalRep = values.rep || sharedRep || initialRep;
        const finalBidType = values.bid_type || sharedBidType || initialBidType;
        let isValid = true;

        if (!values.template) {
          setError('template', { message: 'Please select a template' });
          isValid = false;
        }

        if (!finalRep && showRepField) {
          setError('rep', { message: 'Please select a rep' });
          isValid = false;
        }

        if (!finalBidType && showBidTypeField) {
          setError('bid_type', { message: 'Please select a bid type' });
          isValid = false;
        }

        if (supplier?.integrated) {
          if (!supplierLocation) {
            setError('supplier_location', {
              message: 'Please select a supplier location',
            });
            isValid = false;
          }

          if (supplier.supplier_code === 'SRS_SUPPLY' && !srsJobAccount) {
            setError('srs_job_account', {
              message: 'Please select a SRS job account',
            });
            isValid = false;
          }
        }

        return isValid;
      },
      checkValidity: () => {
        const values = getValues();
        const finalRep = values.rep || sharedRep || initialRep;
        const finalBidType = values.bid_type || sharedBidType || initialBidType;

        if (!values.template) return false;
        if (!finalRep && showRepField) return false;
        if (!finalBidType && showBidTypeField) return false;
        if (supplier?.integrated && !supplierLocation) return false;
        if (
          supplier?.integrated &&
          supplier.supplier_code === 'SRS_SUPPLY' &&
          !srsJobAccount
        )
          return false;

        return true;
      },
      handleSubmit,
      reset,
      setValue,
    }));

    // Handle supplier selection (used by both template and supplier postSelect)
    const handleSupplierSelect = (selected: any) => {
      if (!selected) return;
      setSupplier(selected);
      if (selected.integrated && selected.locations?.length > 0) {
        const selectedLocation = selected.locations[0];
        setSupplierLocation(selectedLocation);
        setValue('supplier_location', selectedLocation?.value);
        if (selectedLocation.job_accounts?.length > 0) {
          const selectedJobAccount = selectedLocation.job_accounts[0];
          setSrsJobAccount(selectedJobAccount);
          setValue('srs_job_account', selectedJobAccount?.value);
        }
      } else {
        setSupplierLocation(null);
        setSrsJobAccount(null);
        setValue('supplier_location', undefined);
        setValue('srs_job_account', undefined);
      }
    };

    const fields = useMemo(() => {
      const _fields: any[] = [
        {
          name: 'template',
          type: FieldType.AsyncSelect,
          forceDropdown: true,
          label: 'Select Template',
          placeholder: 'Select Template',
          error: errors.template?.message,
          control,
          // eslint-disable-next-line react-hooks/rules-of-hooks
          optionsPromise: () => useAllRLTemplateChoices(jobId),
          imageIndicator: (option: any) =>
            option?.version ? (
              <span className="text-xs font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                {option.version}
              </span>
            ) : null,
          postSelect: (selected: any) => {
            // Set the template version
            setTemplateVersion(selected?.version);

            if (selected?.supplier_id) {
              setValue('supplier', selected.supplier_id);
              // Find the supplier from choices and trigger supplier selection logic
              const supplierOption = find(supplierChoices, {
                value: selected.supplier_id,
              });
              if (supplierOption) {
                handleSupplierSelect(supplierOption);
              }
              // Freeze supplier if template is v2
              setIsSupplierFrozen(selected?.version === 'v2');
            } else {
              setSupplier(null);
              setSupplierLocation(null);
              setSrsJobAccount(null);
              setValue('supplier', undefined);
              setValue('supplier_location', undefined);
              setValue('srs_job_account', undefined);
              setIsSupplierFrozen(false);
            }
          },
        },
        {
          name: 'supplier',
          type: FieldType.AsyncSelect,
          label: 'Select Supplier',
          forceDropdown: true,
          orderByLabel: false,
          placeholder: 'Select Supplier',
          error: errors.supplier?.message,
          control,
          disabled: isSupplierFrozen,
          // eslint-disable-next-line react-hooks/rules-of-hooks
          optionsPromise: () => useJobSupplierChoices(jobId),
          postSelect: handleSupplierSelect,
        },
      ];

      if (supplier?.integrated && supplier?.locations?.length > 1) {
        _fields.push({
          name: 'supplier_location',
          type: FieldType.Select,
          label: 'Select Supplier Location',
          options: supplier.locations,
          error: errors.supplier_location?.message,
          control,
          postSelect: (selected: any) => {
            setSupplierLocation(selected);
            if (selected.job_accounts?.length > 0) {
              const selectedJobAccount = selected.job_accounts[0];
              setSrsJobAccount(selectedJobAccount);
              setValue('srs_job_account', selectedJobAccount?.value);
            } else {
              setSrsJobAccount(null);
              setValue('srs_job_account', undefined);
            }
          },
        });
      }

      if (supplier?.integrated && supplierLocation?.job_accounts?.length > 1) {
        _fields.push({
          name: 'srs_job_account',
          type: FieldType.Select,
          label: 'Select SRS Job Account',
          options: supplierLocation.job_accounts,
          error: errors.srs_job_account?.message,
          control,
          postSelect: (selected: any) => {
            setSrsJobAccount(selected);
          },
        });
      }

      // Add color field only for v1 templates
      if (templateVersion !== 'v2') {
        _fields.push({
          name: 'color',
          type: FieldType.AsyncSelect,
          label: 'Select Color',
          placeholder: 'Select Color',
          error: errors.color?.message,
          forceDropdown: true,
          control,
          optionsPromise: useRLColorChoices,
        });
      }

      // Add waste percentage fields for v2 templates
      if (templateVersion === 'v2') {
        _fields.push({
          name: 'waste_pct_choice',
          type: FieldType.Select,
          label:
            'Would you like to use the default waste factors defined in your Roof Template Lineitems, or would you like to override those with a custom waste factor?',
          placeholder: 'Select Waste Factor Choice',
          error: errors.waste_pct_choice?.message,
          control,
          options: [
            { label: 'Default', value: 'default' },
            { label: 'Custom', value: 'custom' },
          ],
        });

        if (wastePctChoice === 'custom') {
          _fields.push({
            name: 'custom_waste_pct',
            type: FieldType.Select,
            label: 'Custom Waste %',
            placeholder: 'Select Waste %',
            error: errors.custom_waste_pct?.message,
            control,
            options: wastePctChoices,
          });
        }
      }

      if (showRepField) {
        _fields.push({
          name: 'rep',
          type: FieldType.AsyncSelect,
          label: 'Select Rep',
          placeholder: 'Select rep',
          error: errors.rep?.message,
          forceDropdown: true,
          control,
          optionsPromise: useRepChoices,
          postSelect: (selected: any) => {
            if (selected?.value && onRepSelect) {
              onRepSelect(selected.value);
            }
          },
        });
      }

      if (showBidTypeField) {
        _fields.push({
          name: 'bid_type',
          type: FieldType.AsyncSelect,
          label: 'Select Bid Type',
          placeholder: 'Select Bid Type',
          error: errors.bid_type?.message,
          forceDropdown: true,
          control,
          optionsPromise: useBidTypeChoices,
          orderByLabel: false,
          postSelect: (selected: any) => {
            if (selected?.value && onBidTypeSelect) {
              onBidTypeSelect(selected.value);
            }
          },
        });
      }

      return _fields;
    }, [
      errors,
      control,
      userInfo,
      supplier,
      supplierLocation,
      srsJobAccount,
      jobId,
      config,
      showRepField,
      showBidTypeField,
      onRepSelect,
      onBidTypeSelect,
      supplierChoices,
      isSupplierFrozen,
      templateVersion,
      wastePctChoice,
    ]);

    // Handle default value assignments in useEffect
    useEffect(() => {
      if (supplier?.integrated && supplier?.locations?.length === 1) {
        setSupplierLocation(supplier.locations[0]);
        setValue('supplier_location', supplier.locations[0]?.value);
      }
    }, [supplier]);

    useEffect(() => {
      if (
        supplier?.integrated &&
        supplierLocation?.job_accounts?.length === 1
      ) {
        setSrsJobAccount(supplierLocation.job_accounts[0]);
        setValue('srs_job_account', supplierLocation.job_accounts[0]?.value);
      }
    }, [supplierLocation, supplier]);

    if (isLoading) {
      return <div className="center h-40">Fetching form config...</div>;
    }

    return (
      <Layout
        fields={processFields(fields, config, [
          'supplier_location',
          'srs_job_account',
        ])}
        loading={loading}
      />
    );
  }
);

DrawingForm.displayName = 'DrawingForm';

export default DrawingForm;
